import {useAui} from '@assistant-ui/react';
import {useEffect, useRef} from 'react';

import {
  useActiveThread,
  useActiveThreadLoadState,
  useIsOnNewChat,
} from '../../agentChat/store/context';
import {useWBAgentWindowStore} from './window/useWBAgentWindowStore';

/**
 * WBAgentSessionRestore reopens the persisted committed thread on a full page
 * load and keeps `restoreThreadId` synced to the displayed committed thread.
 * Renders nothing.
 *
 * NOTE: A component, not a hook, so it can be mounted only when the agent is
 * available; its `useAui()` call would throw without AuiProvider.
 *
 * NOTE: The chat runtime falls back when an archived or deleted thread is
 * missing; this component clears `restoreThreadId` so it is not retried.
 */
type WBAgentSessionRestoreProps = {
  onRestore?: () => void;
  /**
   * Skip the one-shot restore on mount because the host selects the initial
   * thread itself (the Forge header session mounted on the full-page route).
   * Read once at mount; the `restoreThreadId` sync below still runs.
   */
  skipRestore?: boolean;
};

export const WBAgentSessionRestore = ({
  onRestore,
  skipRestore = false,
}: WBAgentSessionRestoreProps) => {
  const aui = useAui();
  const activeThread = useActiveThread();
  const isOnNewChat = useIsOnNewChat();
  const open = useWBAgentWindowStore(s => s.open);
  const setOpen = useWBAgentWindowStore(s => s.setOpen);
  const setRestoreThreadId = useWBAgentWindowStore(s => s.setRestoreThreadId);

  // Capture the persisted target at mount for the one-shot restore below.
  const targetRef = useRef(
    skipRestore ? null : useWBAgentWindowStore.getState().restoreThreadId
  );

  // Restore once on mount. A missing thread is handled by the sync effect.
  useEffect(() => {
    const target = targetRef.current;
    if (target != null) {
      onRestore?.();
      setOpen(true);
      void aui.threads().switchToThread(target);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const threadId = activeThread?.id ?? null;
  const {isLoading, failure} = useActiveThreadLoadState();

  // Sync 'restoreThreadId' to the shown committed thread, skipping the mount
  // run. A not-found load leaves no active thread, so `threadId` is null and
  // the target clears instead of re-persisting into a loop.
  const isHydratedRef = useRef(false);
  useEffect(() => {
    if (!isHydratedRef.current) {
      isHydratedRef.current = true;
      return;
    }

    // NOTE: While a switch loads, the thread isn't in the store yet, so a write
    // here would persist a transient null and lose the restore target.
    if (isLoading) {
      return;
    }

    if (failure?.kind === 'not-found') {
      targetRef.current = null;
      setRestoreThreadId(null);
      return;
    }

    // NOTE: Keep the target after a transient load error so a refresh retries
    // it. An explicit close or a client-only draft clears below.
    if (open && failure?.kind === 'error') {
      return;
    }

    setRestoreThreadId(
      open && !isOnNewChat && threadId != null ? threadId : null
    );
  }, [open, failure, threadId, isLoading, isOnNewChat, setRestoreThreadId]);

  return null;
};
